import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight, Instagram } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { postForm, errorMessage } from "@/lib/api";
import { LOCATIONS } from "@/content/locations";
import { FAQ, LINKS } from "@/content/site";

const EMPTY = { name: "", email: "", phone: "", inquiry_type: "General inquiry", location: "", message: "" };

const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };

const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postForm("/contact", { ...f, phone: f.phone || null, location: f.location || null });
      setDone(true);
      setF(EMPTY);
      toast.success("Thanks for contacting us. We'll get back to you as soon as we can.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-success" className="rounded-[2rem] bg-[#2e9e4f] p-10 text-[#fff5e8]">
        <p className="font-display text-4xl font-extrabold">Message sent!</p>
        <p className="mt-3">Thanks for contacting us. We'll get back to you as soon as we can.</p>
        <button type="button" onClick={() => setDone(false)} className="btn btn-cream mt-8" data-testid="contact-send-another">Send another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="grid gap-5 rounded-[2rem] bg-white p-6 sm:grid-cols-2 sm:p-10">
      <div><label className="label" htmlFor="c-name">Name *</label><input id="c-name" required value={f.name} onChange={set("name")} className="field" data-testid="contact-name-input" /></div>
      <div><label className="label" htmlFor="c-email">Email *</label><input id="c-email" type="email" required value={f.email} onChange={set("email")} className="field" data-testid="contact-email-input" /></div>
      <div><label className="label" htmlFor="c-phone">Phone</label><input id="c-phone" type="tel" value={f.phone} onChange={set("phone")} className="field" data-testid="contact-phone-input" /></div>
      <div>
        <label className="label" htmlFor="c-type">What's this about?</label>
        <select id="c-type" value={f.inquiry_type} onChange={set("inquiry_type")} className="field" data-testid="contact-type-select">
          {["General inquiry", "Event inquiry", "Feedback", "Other"].map((o) => <option key={o}>{o}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className="label" htmlFor="c-loc">Location (optional)</label>
        <select id="c-loc" value={f.location} onChange={set("location")} className="field" data-testid="contact-location-select">
          <option value="">Any / not sure</option>
          {LOCATIONS.map((l) => <option key={l.slug} value={`${l.name} (${l.city})`}>{l.name} — {l.city}</option>)}
        </select>
      </div>
      <div className="sm:col-span-2"><label className="label" htmlFor="c-msg">Message</label><textarea id="c-msg" rows={5} value={f.message} onChange={set("message")} className="field resize-none" data-testid="contact-message-input" /></div>
      <button type="submit" disabled={busy} className="btn btn-berry justify-self-start disabled:opacity-60" data-testid="contact-submit-btn">{busy ? "Sending…" : "Send message"} <ArrowRight className="h-4 w-4" /></button>
    </form>
  );
};

export default function Contact() {
  return (
    <>
      <Seo title="Contact TeaDM — Questions, Events & Feedback" description="Contact TeaDM for general questions, event inquiries and feedback. Email teadmlounge@gmail.com or send us a message. We're in Falls Church, Fairfax, Vienna and Sterling, VA." path="/contact" jsonLd={faqLd} />
      <PageHero testId="contact-hero" bg="#1a0a10" eyebrow="Contact" lines={["Drop us", "a line."]} intro="Questions, event bookings or feedback — we'd love to hear from you.">
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={`mailto:${LINKS.email}`} data-testid="contact-email-link" className="btn btn-berry"><Mail className="h-4 w-4" />{LINKS.email}</a>
          <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="btn btn-ghost text-[#fff5e8]"><Instagram className="h-4 w-4" />Instagram</a>
        </div>
      </PageHero>
      <section className="bg-[#fff5e8] py-20 sm:py-28">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7"><ContactForm /></div>
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight">FAQ</h2>
            <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`q${i}`} className="border-[#1a0a10]/15">
                  <AccordionTrigger className="text-left font-display text-lg font-bold" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base text-[#1a0a10]/80">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-10 rounded-[2rem] bg-[#ffd23f] p-8">
              <p className="font-display text-2xl font-extrabold">Want to work with us?</p>
              <p className="mt-2">We're hiring across our Northern Virginia stores.</p>
              <Link to="/careers" data-testid="contact-careers-link" className="btn btn-ink mt-6">Apply now <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
